import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import {
  AvatarBadge,
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { GenericInputControl } from 'components/RabbitOKRSidePanel/Components/ParentOKRComponent';
import SearchBar from 'components/RabbitOKRSidePanel/Components/SearchBar';
import TooltipLabelList from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/TooltipLabelList';

import { Cross } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import FeedbackSearchEmployees from './FeedbackSearchEmployees';

interface ControlComponentProps {
  isOpen: boolean;
  onToggle: () => void;
  employees: { id: string; displayName: string; profilePhoto: string }[];
  isReadOnly: boolean;
}

const ControlComponent = forwardRef((props: ControlComponentProps, ref) => {
  const { isOpen, onToggle, employees, isReadOnly } = props;
  const noEmployees = employees?.length === 0;
  return (
    <GenericInputControl
      // @ts-ignore
      ref={ref}
      onClick={isReadOnly ? null : onToggle}
      w="100%"
      $isActive={isOpen}
      $isReadOnly={isReadOnly}
    >
      <Tooltip label={noEmployees ? '' : <TooltipLabelList labelList={employees} />}>
        <Box>
          {noEmployees ? (
            <TextBodyMedium>None</TextBodyMedium>
          ) : (
            <AvatarGroup>
              {employees.map(({ id, displayName, profilePhoto }) => (
                <Avatar key={id} name={displayName} src={profilePhoto} mr="4px !important" />
              ))}
            </AvatarGroup>
          )}
        </Box>
      </Tooltip>
    </GenericInputControl>
  );
});

const Footer = ({ onApply, onCancel, isApplyDisabled }) => (
  <Flex justifyContent="space-between" alignItems="center" padding="12px 16px" background="#F5F7F9">
    <Button variant="tertiary" onClick={onCancel}>
      Cancel
    </Button>
    <Button disabled={isApplyDisabled} onClick={onApply}>
      Apply
    </Button>
  </Flex>
);

type Props = {
  selectedEmployees: { id: string; displayName: string; profilePhoto: string }[];
  isReadOnly: boolean;
  searchPlaceholder: string;
  tooltipLabel: string;
  cursor: string;
  onSelect: (selectedEmployees: { id: string; displayName: string; profilePhoto: string }[]) => void;
};

const FeedbackMultiEmployeeSelect = ({
  selectedEmployees = [],
  isReadOnly,
  searchPlaceholder,
  tooltipLabel,
  cursor,
  onSelect,
}: Props) => {
  const [localSelectedEmployees, setLocalSelectedEmployees] = useState(selectedEmployees);

  const { isOpen, onToggle, onClose } = useDisclosure();
  const searchRef = useRef();
  const [searchText, setSearchText] = useState('');

  const handleInputChange = useCallback((e) => setSearchText(e.target.value), []);
  const handleClearInput = useCallback(() => {
    setSearchText('');
  }, []);

  // eslint-disable-next-line no-shadow
  const onLocalSelect = (selectedEmployees) => {
    setLocalSelectedEmployees(selectedEmployees);
  };

  const onDelete = (e, employee) => {
    const isEmployeeAlreadyPresent = localSelectedEmployees.findIndex(({ id }) => id === employee.id) > -1;

    if (isEmployeeAlreadyPresent)
      setLocalSelectedEmployees(localSelectedEmployees.filter(({ id }) => id !== employee.id));
  };

  useEffect(() => {
    setLocalSelectedEmployees(selectedEmployees);
  }, [selectedEmployees]);

  const onCancel = () => {
    handleClearInput();
    setLocalSelectedEmployees(selectedEmployees);
    onToggle();
  };

  const onApply = () => {
    handleClearInput();
    onClose();
    onSelect(localSelectedEmployees);
  };

  return (
    <Box
      w="100%"
      sx={{
        '.chakra-popover__popper': isMobileView()
          ? {
              transform: 'none !important',
              inset: '-40px 0 0 0 !important',
            }
          : {},
      }}
    >
      <Popover
        placement="bottom-start"
        flip
        returnFocusOnClose={false}
        isOpen={isOpen}
        initialFocusRef={searchRef}
        onClose={onCancel}
      >
        {/* @ts-ignore */}
        <PopoverTrigger>
          <ControlComponent employees={selectedEmployees} onToggle={onToggle} isOpen={isOpen} isReadOnly={isReadOnly} />
        </PopoverTrigger>
        <PopoverContent minW={{ base: '100%', md: '420px' }} boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08) !important">
          <Box padding={2}>
            <SearchBar
              value={searchText}
              placeholder={searchPlaceholder}
              onChange={handleInputChange}
              searchRef={searchRef}
              onClearInput={handleClearInput}
            />
            <Box py={3} pl={1}>
              <Flex alignItems="center" columnGap={2}>
                <TextBodyMedium color="#666666" bold>
                  {`Selected (${localSelectedEmployees?.length}) : `}
                </TextBodyMedium>
                <AvatarGroup size={24}>
                  {localSelectedEmployees.map((emp) => {
                    const { id, displayName, profilePhoto } = emp;

                    return (
                      <Avatar isClearable key={id} name={displayName} src={profilePhoto}>
                        <Tooltip label={tooltipLabel}>
                          <AvatarBadge
                            style={{ top: '-10px', bottom: 'unset' }}
                            boxSize="0.9rem"
                            cursor={cursor}
                            onClick={(e) => {
                              onDelete(e, emp);
                            }}
                            bg="white"
                          >
                            <Cross boxSize="12px" color="#666666" />
                          </AvatarBadge>
                        </Tooltip>
                      </Avatar>
                    );
                  })}
                </AvatarGroup>
              </Flex>
            </Box>
            <Divider />
          </Box>
          <PopoverBody px={2} overflowY="auto" height="150px">
            <FeedbackSearchEmployees
              employees={localSelectedEmployees}
              setEmployees={onLocalSelect}
              searchText={searchText}
              atLeastOneEmployeeToBeSelected
            />
          </PopoverBody>
          <Divider />
          <Footer isApplyDisabled={false} onApply={onApply} onCancel={onCancel} />
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default FeedbackMultiEmployeeSelect;
